import { GameState } from '../game/GameState';
import type { CampaignState } from '../types/game';
import { ArmyManager } from './ArmyManager';
import { DataRegistry } from './DataRegistry';
import { DiplomacyManager } from './DiplomacyManager';
import { WarManager } from './WarManager';

export type AIStrategy = 'consolidate' | 'expand' | 'trade' | 'naval' | 'religious' | 'survive';
export type AIEconomicPriority = 'food' | 'infrastructure' | 'trade' | 'military' | 'knowledge';

export interface AIPlanState {
  factionId: string;
  strategy: AIStrategy;
  economicPriority: AIEconomicPriority;
  militaryPosture: 'defensive' | 'balanced' | 'aggressive';
  navalPosture: 'coastal' | 'escort' | 'raiding' | 'dominance';
  targetFactionId?: string;
  targetProvinceId?: string;
  confidence: number;
  riskTolerance: number;
  reserveTarget: number;
  lastReviewedTurn: number;
  reasons: string[];
}

type CampaignWithAI = CampaignState & { aiPlans?: Record<string, AIPlanState> };

export class StrategicAIPlanner {
  static readonly instance = new StrategicAIPlanner();

  ensurePlan(factionId: string, force = false) {
    const campaign = GameState.instance.requireCampaign() as CampaignWithAI;
    campaign.aiPlans ??= {};
    const existing = campaign.aiPlans[factionId];
    const atWar = WarManager.instance.warsForFaction(factionId).length > 0;
    if (!force && existing && campaign.turn - existing.lastReviewedTurn < 6 && !(atWar && existing.strategy !== 'survive')) return existing;
    const plan = this.buildPlan(factionId);
    campaign.aiPlans[factionId] = plan;
    return plan;
  }

  allPlans() {
    const campaign = GameState.instance.requireCampaign() as CampaignWithAI;
    campaign.aiPlans ??= {};
    return campaign.aiPlans;
  }

  private buildPlan(factionId: string): AIPlanState {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const faction = campaign.factions[factionId];
    const data = registry.factions[factionId];
    const provinces = Object.values(campaign.provinces).filter((province) => province.owner === factionId);
    const armies = Object.values(campaign.armies).filter((army) => army.factionId === factionId);
    const ownStrength = armies.reduce((sum, army) => sum + ArmyManager.instance.armyStrength(army.id), 0);
    const food = provinces.reduce((sum, province) => sum + province.foodSecurity, 0) / Math.max(1, provinces.length);
    const prosperity = provinces.reduce((sum, province) => sum + province.prosperity, 0) / Math.max(1, provinces.length);
    const ports = provinces.filter((province) => registry.provinces[province.id]?.port).length;
    const atWar = WarManager.instance.warsForFaction(factionId).length > 0;
    const personality = data?.aiPersonality ?? 'balanced';
    const reasons: string[] = [];

    let strategy: AIStrategy = 'consolidate';
    if (atWar || faction.warExhaustion > 45 || faction.manpower < faction.maxManpower * 0.2) {
      strategy = 'survive';
      reasons.push('War pressure, exhaustion or low manpower requires recovery.');
    } else if (personality.includes('colonial') || personality.includes('sea') || personality.includes('merchant')) {
      strategy = ports > 0 ? 'naval' : 'trade';
      reasons.push('The court personality favors maritime influence and commerce.');
    } else if (personality.includes('devout')) {
      strategy = 'religious';
      reasons.push('The court seeks religious unity and holy-site influence.');
    } else if (personality.includes('expansion') || personality.includes('unifier') || personality.includes('opportunist')) {
      strategy = 'expand';
      reasons.push('The court personality rewards calculated territorial expansion.');
    } else if (prosperity > 62 && faction.treasury > 350) {
      strategy = 'trade';
      reasons.push('A stable treasury and prosperous provinces favor commercial growth.');
    }

    let economicPriority: AIEconomicPriority = 'infrastructure';
    if (food < 58) { economicPriority = 'food'; reasons.push('Average food security is below the safe threshold.'); }
    else if (atWar || ownStrength < provinces.length * 450) economicPriority = 'military';
    else if (strategy === 'trade' || strategy === 'naval') economicPriority = 'trade';
    else if (faction.research.adoptedInstitutions.length < Math.max(1, Math.floor(campaign.turn / 36))) economicPriority = 'knowledge';

    const target = this.selectTarget(factionId, strategy);
    if (target) reasons.push(`Strategic pressure is focused on ${registry.provinces[target.provinceId]?.name ?? target.provinceId}.`);
    const relativeThreat = target ? this.factionStrength(target.factionId) / Math.max(1, ownStrength) : 0.7;
    const confidence = Math.max(10, Math.min(95, Math.round(60 + (1 - relativeThreat) * 28 - faction.warExhaustion * 0.45)));
    const riskTolerance = Math.max(15, Math.min(90, 35 + (personality.includes('expansion') || personality.includes('raider') ? 28 : 0) + faction.prestige * 0.12 - faction.warExhaustion * 0.5));

    return {
      factionId,
      strategy,
      economicPriority,
      militaryPosture: strategy === 'survive' ? 'defensive' : strategy === 'expand' || strategy === 'religious' ? 'aggressive' : 'balanced',
      navalPosture: strategy === 'naval' ? 'dominance' : strategy === 'trade' ? 'escort' : personality.includes('raider') ? 'raiding' : 'coastal',
      targetFactionId: target?.factionId,
      targetProvinceId: target?.provinceId,
      confidence,
      riskTolerance,
      reserveTarget: Math.round(Math.max(80, provinces.length * 35 + faction.maxManpower * (strategy === 'survive' ? 0.22 : 0.12))),
      lastReviewedTurn: campaign.turn,
      reasons: reasons.slice(0, 5),
    };
  }

  private selectTarget(factionId: string, strategy: AIStrategy) {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const faction = campaign.factions[factionId];
    const owned = Object.values(campaign.provinces).filter((province) => province.owner === factionId);
    const neighbors = new Set(owned.flatMap((province) => registry.provinces[province.id]?.neighbors ?? []));
    const candidates = [...neighbors]
      .map((provinceId) => campaign.provinces[provinceId])
      .filter((province): province is NonNullable<typeof province> => Boolean(province && province.owner !== factionId && campaign.factions[province.owner]));
    const scored = candidates.map((province) => {
      const data = registry.provinces[province.id];
      const opinion = DiplomacyManager.instance.opinion(factionId, province.owner);
      const claim = faction.claims.includes(province.id) ? 45 : 0;
      const value = province.prosperity * 0.45 + province.population * 0.25 + (data?.port ? 18 : 0) + (data?.deposits?.length ?? 0) * 5;
      const identity = strategy === 'religious' && province.religion !== faction.stateReligion ? 24 : 0;
      const commercial = (strategy === 'trade' || strategy === 'naval') && data?.port ? 28 : 0;
      const diplomacy = opinion < -30 ? 18 : opinion > 25 ? -50 : 0;
      const treatyPenalty = faction.treaties.alliances.includes(province.owner) || faction.treaties.nonAggression.includes(province.owner) ? -100 : 0;
      return { factionId: province.owner, provinceId: province.id, score: claim + value + identity + commercial + diplomacy + treatyPenalty - this.factionStrength(province.owner) * 0.001 };
    }).sort((a, b) => b.score - a.score);
    return scored[0];
  }

  private factionStrength(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    return Object.values(campaign.armies)
      .filter((army) => army.factionId === factionId)
      .reduce((sum, army) => sum + ArmyManager.instance.armyStrength(army.id), 0);
  }
}
